/**
 * @fileoverview `<ReasonTextarea>` — saisie de motif pour actions sensibles.
 *
 * Rejeu STI, remboursement et réinitialisation MFA exigent un motif explicite
 * côté backoffice. La règle de longueur vient du contrat API ; le composant
 * l'affiche en amont pour éviter une action opaque au clic.
 */

import * as React from 'react';
import { cn } from '../lib/utils.ts';

export interface ReasonTextareaProps extends React.ComponentProps<'textarea'> {
  /** Longueur minimale contractuelle. Défaut : 10 caractères. */
  minReasonLength?: number;
  /** Affiche le compteur sous le champ. Défaut : `true`. */
  showCounter?: boolean;
}

/**
 * Zone de motif standard pour les actions sensibles.
 *
 * @param props - Props textarea natives, longueur minimale et compteur.
 * @returns Textarea avec compteur et style GNPC.
 *
 * @example
 * ```tsx
 * <ReasonTextarea value={reason} onChange={(event) => setReason(event.target.value)} />
 * ```
 */
export const ReasonTextarea = React.forwardRef<HTMLTextAreaElement, ReasonTextareaProps>(
  function ReasonTextarea(
    {
      className,
      minReasonLength = 10,
      showCounter = true,
      value,
      minLength,
      rows = 4,
      ...props
    },
    ref,
  ) {
    const length = typeof value === 'string' ? value.trim().length : 0;
    const requiredLength = minLength ?? minReasonLength;
    const valid = length >= requiredLength;

    return (
      <div className="grid gap-1.5">
        <textarea
          ref={ref}
          rows={rows}
          value={value}
          minLength={requiredLength}
          className={cn(
            'border-input bg-background text-foreground placeholder:text-muted-foreground flex min-h-24 w-full rounded-md border px-3.5 py-3 text-sm outline-none transition-[color,box-shadow]',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...props}
        />
        {showCounter ? (
          <p className={cn('text-xs', valid ? 'text-muted-foreground' : 'text-warning')}>
            {length}/{requiredLength} caractères minimum
          </p>
        ) : null}
      </div>
    );
  },
);
